/**
 * Work-week utilities shared between client and server.
 *
 * Day indexing is 0..6 with 0 = Sunday, 1 = Monday, ..., 6 = Saturday.
 * `workingDays` is a bitmask: bit i set => day i is a working day.
 */

export const DAY_NAMES_EN = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const DAY_NAMES_SHORT_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
export const DAY_NAMES_SHORT_AR = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"] as const;

export const WORKWEEK_PRESETS = {
  GCC_SUN_THU: 0b0011111,         // Sun..Thu = 31
  WESTERN_MON_FRI: 0b0111110,     // Mon..Fri = 62
  ISRAELI_SUN_THU: 0b0011111,
  SAT_THU: 0b1011111,             // some MENA: Sat..Thu = 95 (no Friday)
  MON_SAT: 0b1111110,             // 6-day Mon..Sat = 126
  MON_SUN: 0b1111111,             // 7-day = 127
} as const;

export function isWorkingDay(workingDays: number, dayIndex: number): boolean {
  return ((workingDays >> dayIndex) & 1) === 1;
}

export function workingDaysList(workingDays: number, weekStart = 0): number[] {
  const out: number[] = [];
  for (let i = 0; i < 7; i++) {
    const d = (weekStart + i) % 7;
    if (isWorkingDay(workingDays, d)) out.push(d);
  }
  return out;
}

export function orderedWeekDays(weekStart = 0): number[] {
  return Array.from({ length: 7 }, (_, i) => (weekStart + i) % 7);
}

/** ISO YYYY-MM-DD in UTC for a Date */
export function isoDateUTC(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/** Returns the week-start date (UTC) for `d` given `weekStartDay` (0..6). */
export function weekStartUTC(d: Date, weekStartDay = 0): Date {
  const day = d.getUTCDay();
  const delta = (day - weekStartDay + 7) % 7;
  const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  start.setUTCDate(start.getUTCDate() - delta);
  return start;
}

/** Stable string key used as `periodKey` for weekly receipts. */
export function weekKey(d: Date, weekStartDay = 0): string {
  return isoDateUTC(weekStartUTC(d, weekStartDay));
}

/** Returns array of 7 dates starting from the week start. */
export function weekDates(d: Date, weekStartDay = 0): Date[] {
  const start = weekStartUTC(d, weekStartDay);
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(start);
    x.setUTCDate(start.getUTCDate() + i);
    return x;
  });
}

export function dayLabel(dayIndex: number, locale: "en" | "ar" = "en"): string {
  return locale === "ar" ? DAY_NAMES_SHORT_AR[dayIndex] : DAY_NAMES_SHORT_EN[dayIndex];
}

/** A Hijri formatter that uses the platform Intl when available, otherwise empty string. */
export function formatHijri(d: Date, locale: "en" | "ar" = "en"): string {
  try {
    const fmt = new Intl.DateTimeFormat(
      locale === "ar" ? "ar-SA-u-ca-islamic" : "en-u-ca-islamic-umalqura",
      { day: "numeric", month: "long", year: "numeric" },
    );
    return fmt.format(d);
  } catch {
    return "";
  }
}


/* ──────────────────────────────────────────────────────────────────────────
 *  Ramadan-aware capacity
 *
 *  When `ramadanAware === true` and the calendar day falls inside Ramadan,
 *  the user's expected daily capacity is reduced by `RAMADAN_CAPACITY_RATIO`
 *  (default 0.7 = 70%, a common GCC working-hours convention).
 *
 *  The Hijri date is read via `Intl.DateTimeFormat("...islamic-umalqura")`
 *  so we don't need a separate Hijri library. Month 9 in the Islamic
 *  calendar is Ramadan.
 * ────────────────────────────────────────────────────────────────────────── */

export const RAMADAN_CAPACITY_RATIO = 0.7;
export const DEFAULT_DAILY_CAPACITY_HOURS = 8;

/** True when the given Gregorian date falls within the Hijri month of Ramadan. */
export function isRamadanDay(d: Date): boolean {
  try {
    const fmt = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", { month: "numeric" });
    const parts = fmt.formatToParts(d);
    const monthStr = parts.find(p => p.type === "month")?.value ?? "";
    return Number(monthStr) === 9;
  } catch {
    return false;
  }
}

/**
 * Returns the user's effective daily capacity (in hours) for `d`.
 * If Ramadan-aware mode is on and `d` is in Ramadan, capacity is reduced.
 */
export function dailyCapacityHours(
  d: Date,
  opts: { ramadanAware: boolean; baseHours?: number } = { ramadanAware: false },
): number {
  const base = opts.baseHours ?? DEFAULT_DAILY_CAPACITY_HOURS;
  if (opts.ramadanAware && isRamadanDay(d)) {
    return Math.round(base * RAMADAN_CAPACITY_RATIO * 10) / 10;
  }
  return base;
}

/** Returns total expected hours for the working days inside `weekStart`'s 7-day window. */
export function weekCapacityHours(
  weekStart: Date,
  workingDays: number,
  weekStartDay: number,
  opts: { ramadanAware: boolean; baseHours?: number } = { ramadanAware: false },
): number {
  const dates = weekDates(weekStart, weekStartDay);
  let total = 0;
  for (const d of dates) {
    const dayIdx = d.getUTCDay();
    if (!isWorkingDay(workingDays, dayIdx)) continue;
    total += dailyCapacityHours(d, opts);
  }
  return Math.round(total * 10) / 10;
}
